import React, { useEffect, useRef, useState } from "react";
import { Type, Image as ImageIcon, Trash2, Send, X, Loader2, Minus, Plus, Palette } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const PRODUCTS = [
  { key: "tshirt", label: "T-Shirt", mockup: null, shape: "tshirt" },
  { key: "backpack", label: "Backpack", mockup: null, shape: "backpack" },
  { key: "bag", label: "Tote Bag", mockup: null, shape: "tote" },
];

// Reusable product silhouettes - no external photography, they take the selected base color
const SILHOUETTES = {
  tshirt: (
    <svg viewBox="0 0 200 250" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%" }}>
      <path d="M60 30 C60 20 80 12 100 12 C120 12 140 20 140 30 L172 45 L188 90 L162 100 L162 220 C162 232 154 240 142 240 L58 240 C46 240 38 232 38 220 L38 100 L12 90 L28 45 Z" fill="currentColor"/>
    </svg>
  ),
  backpack: (
    <svg viewBox="0 0 200 260" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%" }}>
      <path d="M70 30 C70 15 85 8 100 8 C115 8 130 15 130 30 L138 40 C160 44 176 62 176 88 L176 220 C176 236 164 248 148 248 L52 248 C36 248 24 236 24 220 L24 88 C24 62 40 44 62 40 Z M55 110 L145 110 L145 155 L55 155 Z" fill="currentColor"/>
    </svg>
  ),
  tote: (
    <svg viewBox="0 0 200 240" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%" }}>
      <path d="M70 20 C70 12 78 6 88 6 L112 6 C122 6 130 12 130 20 L130 55 M30 60 L170 60 L182 220 C183 230 174 238 164 238 L36 238 C26 238 17 230 18 220 Z" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M30 60 L170 60 L182 220 C183 230 174 238 164 238 L36 238 C26 238 17 230 18 220 Z" fill="currentColor"/>
    </svg>
  ),
};

// Product base colors that map to blend overlays on the mockup
const BASE_COLORS = [
  { name: "Black",  hex: "#111111" },
  { name: "White",  hex: "#F2F2F2" },
  { name: "Charcoal", hex: "#3A3A3A" },
  { name: "Sand",   hex: "#D5C6A4" },
  { name: "Cream",  hex: "#F5EBD8" },
  { name: "Pink",   hex: "#EC008C" },
  { name: "Yellow", hex: "#F3E30A" },
  { name: "Lime",   hex: "#CCDE00" },
  { name: "Red",    hex: "#C1272D" },
  { name: "Navy",   hex: "#1E2A4A" },
  { name: "Green",  hex: "#0E7C4A" },
  { name: "Purple", hex: "#5D3FD3" },
];

const FONTS = ["Montserrat", "Caveat", "Impact", "Georgia", "Courier New"];
const TEXT_COLORS = ["#000000", "#FFFFFF", "#EC008C", "#F3E30A", "#CCDE00", "#C1272D", "#1E2A4A", "#5D3FD3"];

const Designer = () => {
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const [product, setProduct] = useState("tshirt");
  const [baseColor, setBaseColor] = useState(BASE_COLORS[0]);
  const [elements, setElements] = useState([]);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [customer, setCustomer] = useState({ name: "", email: "", notes: "" });
  const [quantity, setQuantity] = useState(1);
  const [showSubmit, setShowSubmit] = useState(false);

  const activeProduct = PRODUCTS.find((p) => p.key === product);
  const isLightBase = ["#F2F2F2", "#F5EBD8", "#D5C6A4", "#F3E30A", "#CCDE00"].includes(baseColor.hex);

  const addText = () => {
    const id = `el-${Date.now()}`;
    setElements([...elements, {
      id, type: "text", x: 40, y: 40, w: 60, h: 20,
      text: "Your Text", color: isLightBase ? "#000000" : "#FFFFFF", font: "Montserrat", size: 28, weight: "800",
    }]);
    setSelected(id);
  };

  const addImage = (dataUrl) => {
    const id = `el-${Date.now()}`;
    setElements([...elements, { id, type: "image", x: 30, y: 30, w: 40, h: 40, src: dataUrl }]);
    setSelected(id);
  };

  const onFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => addImage(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const updateEl = (id, patch) => setElements((prev) => prev.map((el) => (el.id === id ? { ...el, ...patch } : el)));
  const removeEl = (id) => {
    setElements((prev) => prev.filter((el) => el.id !== id));
    if (selected === id) setSelected(null);
  };

  // Drag & resize
  const onMouseDown = (e, el, mode = "move") => {
    e.stopPropagation();
    setSelected(el.id);
    const canvas = canvasRef.current.getBoundingClientRect();
    dragRef.current = {
      id: el.id, mode,
      startX: e.clientX, startY: e.clientY,
      origX: el.x, origY: el.y, origW: el.w, origH: el.h,
      canvasW: canvas.width, canvasH: canvas.height,
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  const onMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dxPct = ((e.clientX - d.startX) / d.canvasW) * 100;
    const dyPct = ((e.clientY - d.startY) / d.canvasH) * 100;
    if (d.mode === "move") {
      updateEl(d.id, {
        x: Math.min(95, Math.max(0, d.origX + dxPct)),
        y: Math.min(95, Math.max(0, d.origY + dyPct)),
      });
    } else if (d.mode === "resize") {
      updateEl(d.id, {
        w: Math.max(10, d.origW + dxPct),
        h: Math.max(5, d.origH + dyPct),
      });
    }
  };
  const onUp = () => {
    dragRef.current = null;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", onUp);
  };
  useEffect(() => () => {
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", onUp);
  }, []);

  const capturePreview = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return "";
    const rect = canvas.getBoundingClientRect();
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${rect.width}" height="${rect.height}"><foreignObject width="100%" height="100%"><div xmlns="http://www.w3.org/1999/xhtml">${new XMLSerializer().serializeToString(canvas)}</div></foreignObject></svg>`;
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
  };

  const handleSubmit = async () => {
    if (!customer.name || !customer.email) {
      toast.error("Name and email required");
      return;
    }
    if (!elements.length) {
      toast.error("Add at least one element to your design");
      return;
    }
    setSubmitting(true);
    try {
      const preview = await capturePreview();
      await api.post("/custom-orders", {
        product_type: product,
        design_preview: preview.slice(0, 5000),
        design_config: { elements, product, baseColor: baseColor.name, quantity },
        customer_email: customer.email,
        customer_name: customer.name,
        notes: `${customer.notes}\nProduct: ${activeProduct.label} · Color: ${baseColor.name} · Qty: ${quantity}`,
        quantity,
      });
      toast.success("Design submitted! Makeba will reach out with a quote soon.");
      setElements([]);
      setShowSubmit(false);
      setCustomer({ name: "", email: "", notes: "" });
    } catch {
      toast.error("Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedEl = elements.find((e) => e.id === selected);

  return (
    <main data-testid="designer-page" className="container-x py-10 md:py-14">
      <div className="mb-8 md:mb-10">
        <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-semibold mb-3">Design Studio</div>
        <h1 className="font-display font-light text-4xl md:text-6xl text-black tracking-tight">
          Make it <span className="font-black">yours.</span>
        </h1>
        <p className="mt-3 text-[#4A4A4A] max-w-2xl font-light">
          Choose a product, pick your base color, upload artwork, add text, and set your quantity.
          Submit your design and we'll bring it to life on premium apparel.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* LEFT PANEL */}
        <aside className="lg:col-span-3 space-y-5">
          {/* Product type */}
          <div className="border border-[#E5E2DA] p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-black mb-3">Product</div>
            <div className="grid grid-cols-3 gap-2">
              {PRODUCTS.map((p) => (
                <button
                  key={p.key}
                  data-testid={`product-type-${p.key}`}
                  onClick={() => setProduct(p.key)}
                  className={`p-2 border text-[10px] tracking-[0.15em] uppercase font-bold transition ${
                    product === p.key ? "bg-black text-white border-black" : "bg-white text-black border-[#E5E2DA] hover:border-black"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* PRODUCT BASE COLOR PICKER */}
          <div className="border border-[#E5E2DA] p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-black">Product Color</div>
              <div className="text-[10px] uppercase tracking-wider text-[#4A4A4A]">{baseColor.name}</div>
            </div>
            <div className="grid grid-cols-6 gap-2">
              {BASE_COLORS.map((c) => (
                <button
                  key={c.name}
                  data-testid={`base-color-${c.name.toLowerCase()}`}
                  onClick={() => setBaseColor(c)}
                  title={c.name}
                  className={`w-full aspect-square rounded-full border-2 transition ${
                    baseColor.hex === c.hex ? "border-[#EC008C] ring-2 ring-[#EC008C]" : "border-[#E5E2DA] hover:border-black"
                  }`}
                  style={{ background: c.hex }}
                />
              ))}
            </div>
          </div>

          {/* QUANTITY PICKER */}
          <div className="border border-[#E5E2DA] p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-black mb-3">Quantity</div>
            <div className="flex items-center gap-2">
              <button
                data-testid="qty-minus"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-10 h-10 border border-black hover:bg-black hover:text-white flex items-center justify-center"
              >
                <Minus size={14} />
              </button>
              <input
                data-testid="qty-input"
                type="number"
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="flex-1 h-10 border border-black text-center font-bold focus:outline-none focus:border-[#EC008C]"
              />
              <button
                data-testid="qty-plus"
                onClick={() => setQuantity(quantity + 1)}
                className="w-10 h-10 border border-black hover:bg-black hover:text-white flex items-center justify-center"
              >
                <Plus size={14} />
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {[1, 5, 10, 25, 50, 100].map((q) => (
                <button
                  key={q}
                  data-testid={`qty-preset-${q}`}
                  onClick={() => setQuantity(q)}
                  className={`text-[10px] uppercase tracking-wider px-2 py-1 border transition ${
                    quantity === q ? "bg-[#EC008C] text-white border-[#EC008C]" : "border-[#E5E2DA] text-[#4A4A4A] hover:border-black"
                  }`}
                >
                  {q}+
                </button>
              ))}
            </div>
            <div className="mt-3 text-[10px] uppercase tracking-wider text-[#4A4A4A]">
              Bulk pricing available for 25+
            </div>
          </div>

          {/* ADD ELEMENTS */}
          <div className="border border-[#E5E2DA] p-5 space-y-2">
            <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-black mb-3">Add elements</div>
            <button
              data-testid="add-text-btn"
              onClick={addText}
              className="w-full flex items-center gap-2 px-4 py-3 border border-black text-[11px] tracking-[0.18em] uppercase font-bold hover:bg-black hover:text-white transition"
            >
              <Type size={14} /> Add Text
            </button>
            <label
              data-testid="add-image-label"
              className="w-full flex items-center gap-2 px-4 py-3 bg-[#EC008C] text-white border border-[#EC008C] text-[11px] tracking-[0.18em] uppercase font-bold cursor-pointer hover:bg-black hover:border-black transition"
            >
              <ImageIcon size={14} /> Upload Image
              <input type="file" data-testid="upload-image-input" accept="image/*" onChange={onFileUpload} className="hidden" />
            </label>
          </div>

          {/* Element inspector */}
          {selectedEl && (
            <div className="border border-black bg-[#FAFAF7] p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-black">Selected element</div>
                <button data-testid="remove-element-btn" onClick={() => removeEl(selectedEl.id)} className="text-[#EC008C] hover:text-black">
                  <Trash2 size={16} />
                </button>
              </div>

              {selectedEl.type === "text" && (
                <>
                  <input
                    data-testid="edit-text-input"
                    value={selectedEl.text}
                    onChange={(e) => updateEl(selectedEl.id, { text: e.target.value })}
                    className="w-full px-3 py-2 border border-black text-sm focus:outline-none focus:border-[#EC008C]"
                  />
                  <div>
                    <div className="text-[10px] tracking-wider uppercase font-semibold text-[#4A4A4A] mb-1">Font</div>
                    <select
                      value={selectedEl.font}
                      onChange={(e) => updateEl(selectedEl.id, { font: e.target.value })}
                      className="w-full px-3 py-2 border border-black text-sm"
                    >
                      {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-wider uppercase font-semibold text-[#4A4A4A] mb-1">Size: {selectedEl.size}px</div>
                    <input type="range" min={12} max={90} value={selectedEl.size} onChange={(e) => updateEl(selectedEl.id, { size: +e.target.value })} className="w-full" />
                  </div>
                  <div>
                    <div className="text-[10px] tracking-wider uppercase font-semibold text-[#4A4A4A] mb-1">Text color</div>
                    <div className="flex flex-wrap gap-2">
                      {TEXT_COLORS.map((c) => (
                        <button
                          key={c}
                          onClick={() => updateEl(selectedEl.id, { color: c })}
                          className={`w-7 h-7 rounded-full border-2 ${selectedEl.color === c ? "border-[#EC008C] ring-2 ring-[#EC008C]" : "border-black"}`}
                          style={{ background: c }}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}

              {selectedEl.type === "image" && (
                <div className="text-xs text-[#4A4A4A]">Drag to move · corner handle to resize</div>
              )}
            </div>
          )}
        </aside>

        {/* CANVAS */}
        <div className="lg:col-span-6">
          <div className="mb-3 flex items-center justify-between text-[11px] tracking-[0.22em] uppercase font-semibold">
            <div className="flex items-center gap-2 text-black">
              <Palette size={14} /> {activeProduct.label} · <span style={{ color: baseColor.hex === "#F2F2F2" ? "#111" : baseColor.hex }}>{baseColor.name}</span>
            </div>
            <div className="text-[#4A4A4A]">Qty: <span className="text-black">{quantity}</span></div>
          </div>

          <div
            ref={canvasRef}
            data-testid="designer-canvas"
            className="relative overflow-hidden aspect-[4/5] select-none border border-black bg-[#FAFAF7]"
            onClick={() => setSelected(null)}
          >
            {/* Product silhouette in the design garment shape - ONLY the silhouette takes the color */}
            <div className="absolute inset-0 flex items-center justify-center p-6" style={{ color: baseColor.hex }}>
              {SILHOUETTES[activeProduct.shape]}
            </div>
            {/* subtle canvas grid for depth */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: "radial-gradient(#000 1px, transparent 1px)", backgroundSize: "16px 16px" }} />

            <div className="absolute inset-x-[15%] top-[22%] bottom-[15%] border-2 border-dashed border-white/60 rounded-md pointer-events-none flex items-center justify-center">
              {elements.length === 0 && (
                <div
                  className="font-semibold text-xs uppercase tracking-[0.25em] text-center px-4"
                  style={{ color: isLightBase ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.7)" }}
                >
                  Design area - add text or images
                </div>
              )}
            </div>

            {elements.map((el) => (
              <div
                key={el.id}
                data-testid={`el-${el.id}`}
                onMouseDown={(e) => onMouseDown(e, el, "move")}
                className={`absolute cursor-move ${selected === el.id ? "outline outline-2 outline-[#EC008C]" : ""}`}
                style={{ left: `${el.x}%`, top: `${el.y}%`, width: `${el.w}%`, minHeight: 20 }}
              >
                {el.type === "text" ? (
                  <div
                    style={{
                      fontFamily: el.font,
                      color: el.color,
                      fontSize: `${el.size}px`,
                      fontWeight: el.weight,
                      lineHeight: 1.1,
                      textShadow: "0 1px 0 rgba(0,0,0,0.05)",
                      pointerEvents: "none",
                    }}
                  >
                    {el.text}
                  </div>
                ) : (
                  <img src={el.src} alt="upload" className="w-full h-full object-contain pointer-events-none" />
                )}
                {selected === el.id && (
                  <div
                    onMouseDown={(e) => onMouseDown(e, el, "resize")}
                    className="absolute -right-2 -bottom-2 w-4 h-4 bg-[#EC008C] border-2 border-black rounded-sm cursor-se-resize"
                  />
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#4A4A4A]">
              Est. total: <span className="text-black font-bold">${(29.99 * quantity).toFixed(2)}</span>{quantity >= 25 && <span className="ml-2 text-[#EC008C]">(bulk pricing available)</span>}
            </div>
            <button
              data-testid="submit-design-btn"
              onClick={() => setShowSubmit(true)}
              disabled={!elements.length}
              className="btn-primary disabled:opacity-50"
            >
              <Send size={14} /> Submit for Quote
            </button>
          </div>
        </div>

        {/* RIGHT PANEL - how it works */}
        <aside className="lg:col-span-3 space-y-5">
          <div className="border border-[#E5E2DA] bg-[#FAFAF7] p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-black mb-3">How it works</div>
            <ol className="text-sm text-[#4A4A4A] space-y-2.5 list-decimal ml-4 font-light">
              <li>Pick product type</li>
              <li>Choose product color</li>
              <li>Set quantity</li>
              <li>Add text &amp; upload art</li>
              <li>Drag &amp; resize to place</li>
              <li>Submit for our team review</li>
              <li>Get quote &amp; approve</li>
              <li>Receive your custom pieces!</li>
            </ol>
          </div>
          <div className="border border-black bg-[#EC008C] text-white p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase font-semibold mb-2">Bulk order?</div>
            <p className="text-sm font-light leading-relaxed">Ordering for a team, school, or event? We handle 10 to 10,000 - same care, better pricing.</p>
          </div>
        </aside>
      </div>

      {/* Submit modal */}
      {showSubmit && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowSubmit(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-black p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-bold text-xl uppercase tracking-tight">Submit your design</h3>
              <button data-testid="close-submit-modal" onClick={() => setShowSubmit(false)}><X /></button>
            </div>

            <div className="mb-4 p-3 border border-[#E5E2DA] bg-[#FAFAF7] text-[11px] tracking-wider uppercase text-[#4A4A4A] space-y-1">
              <div>Product: <strong className="text-black">{activeProduct.label}</strong></div>
              <div>Color: <strong className="text-black">{baseColor.name}</strong></div>
              <div>Quantity: <strong className="text-black">{quantity}</strong></div>
              <div>Elements: <strong className="text-black">{elements.length}</strong></div>
            </div>

            <div className="space-y-3">
              <input
                data-testid="submit-name"
                placeholder="Your name"
                value={customer.name}
                onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                className="w-full px-4 py-3 border border-black focus:outline-none focus:border-[#EC008C]"
              />
              <input
                data-testid="submit-email"
                type="email"
                placeholder="Email"
                value={customer.email}
                onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                className="w-full px-4 py-3 border border-black focus:outline-none focus:border-[#EC008C]"
              />
              <textarea
                data-testid="submit-notes"
                placeholder="Notes (deadline, sizes, extra details, etc.)"
                value={customer.notes}
                onChange={(e) => setCustomer({ ...customer, notes: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 border border-black focus:outline-none focus:border-[#EC008C]"
              />
              <button
                data-testid="submit-confirm-btn"
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full btn-primary justify-center disabled:opacity-60"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <><Send size={14}/> Send to Makeba</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Designer;
